import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import "./Board.css";
gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

// Miro-style board pieces shared by the Services cards and case-study charts.
// A board is an SVG (viewBox 400x260) on a dotted canvas: sticky nodes,
// diamond decisions, a token riding hidden route paths, and a cursor.

export { token } from "../../utils/tokens";

// Loops the given timelines while the board is on screen, pauses off screen
export const autoplay = (trigger, ...anims) => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  ScrollTrigger.create({
    trigger,
    start: "top 90%",
    end: "bottom 10%",
    onToggle: (self) => anims.forEach((a) => (self.isActive ? a.play() : a.pause())),
  });
};

// The dot rides hidden centre-to-centre route paths at one constant speed,
// so long and short edges feel the same and curves follow the drawn connector
const DOT_SPEED = 190; // board units per second
export const travel = (tl, route, position) =>
  tl.to(".board-token", { motionPath: { path: route }, duration: route.getTotalLength() / DOT_SPEED, ease: "sine.inOut" }, position);

// Cursor "press": quick squash at the pointer tip
export const press = (tl, at) =>
  tl.to(".board-cursor-body", { scale: 0.8, duration: 0.08, yoyo: true, repeat: 1, transformOrigin: "0 0", ease: "power1.inOut" }, at);

// Springy "landed" pop. Runs as a native animation on the inner .board-pop,
// whose CSS (transform-box: fill-box; origin: center) keeps it centred.
// GSAP scaling an SVG <g> uses a cached bbox origin and drifts the node.
export const popIn = (tl, el, position) =>
  tl.call(() => {
    const target = el.matches(".board-pop") ? el : el.querySelector(".board-pop");
    target.animate([{ transform: "scale(0.7)" }, { transform: "scale(1.08)", offset: 0.6 }, { transform: "scale(1)" }], {
      duration: 600,
      easing: "ease-out",
    });
  }, null, position);

export const Cursor = () => (
  <g className="board-cursor">
    <g className="board-cursor-body">
      <path d="M0 0 V16 L4.5 12 L7.5 19 L10 18 L7 11 H13 Z" />
      <rect x="10" y="20" width="46" height="18" rx="2" />
      <text x="33" y="29" className="board-text board-text--sm">Ayoub</text>
    </g>
  </g>
);

export const Arrows = ({ id }) => (
  <defs>
    <marker id={`${id}-arrow`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
      <path d="M0 0 L10 5 L0 10 Z" className="board-arrowhead" />
    </marker>
    <marker id={`${id}-arrow-on`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
      <path d="M0 0 L10 5 L0 10 Z" className="board-arrowhead board-arrowhead--on" />
    </marker>
  </defs>
);

// Icon stacked over a label, centred on the node's origin
export const NodeLabel = ({ icon: Icon, children, dark }) => (
  <>
    <Icon x={-8} y={-17} size={16} stroke={1.75} />
    <text y="9" className={`board-text${dark ? "" : " board-text--light"}`}>{children}</text>
  </>
);

// "AI" node: spinning gradient fill, soft glow and a ✦. Centred on its origin.
// Animate .board-ai-gradient / .board-ai-icon / .board-ai-glow with `aiThink`.
export const AiNode = ({ id, label = "AI draft", w = 80, h = 64 }) => (
  <g className="board-ai-node">
    <defs>
      <linearGradient id={`${id}-ai`} className="board-ai-gradient" gradientTransform="rotate(0 0.5 0.5)">
        <stop offset="0" className="board-ai-stop-1" />
        <stop offset="0.5" className="board-ai-stop-2" />
        <stop offset="1" className="board-ai-stop-3" />
      </linearGradient>
      <filter id={`${id}-blur`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="10" />
      </filter>
    </defs>
    <rect className="board-ai-glow" x={-w / 2} y={-h / 2} width={w} height={h} fill={`url(#${id}-ai)`} filter={`url(#${id}-blur)`} />
    <g className="board-pop board-filled">
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx="2" style={{ fill: `url(#${id}-ai)` }} />
      <path
        className="board-ai-icon"
        d="M0 -18 C1 -12 2 -11 8 -10 C2 -9 1 -8 0 -2 C-1 -8 -2 -9 -8 -10 C-2 -11 -1 -12 0 -18 Z"
      />
      <text y="9" className="board-text">{label}</text>
    </g>
  </g>
);

// AI is "reasoning" the whole time: gradient spins, the ✦ turns, glow breathes
export const aiThink = () =>
  gsap
    .timeline({ paused: true, repeat: -1 })
    .to(".board-ai-gradient", { attr: { gradientTransform: "rotate(360 0.5 0.5)" }, duration: 4, ease: "none" }, 0)
    .to(".board-ai-icon", { rotation: 360, transformOrigin: "50% 50%", duration: 1.6, repeat: 1, repeatDelay: 0.4, ease: "power3.inOut" }, 0)
    .to(".board-ai-glow", { opacity: 0.9, duration: 1, yoyo: true, repeat: 3, ease: "sine.inOut" }, 0);
